/* eslint-disable max-lines */ //disabled for placeholderdata, will be removing when api implements
import * as React from 'react'
import ReactDOMServer from 'react-dom/server'
import {
  ChartSvg,
  CompletedCellContainer,
  SelectAllHeaderCell,
  TextCell,
} from 'routingAndDispatch/common/RouteTimeTable/routePlanTimeTableStyles'
import {
  CheckCircle,
  House,
  FlagCheckered,
  CheckSquare,
  Square,
  Truck,
} from 'phosphor-react'
import {select, scaleTime, axisBottom, timeFormat} from 'd3'
import {
  addMinutes,
  differenceInHours,
  eachDayOfInterval,
  format,
  getMinutes,
  isSameDay,
  parse,
  startOfDay,
} from 'date-fns'
import {UserConfig, useStore} from 'common/useStore'
import RouteTimeTableActionButtons from './TableActionButtons'
import {
  RoutePlanType,
  StopType,
  Segment,
} from 'routingAndDispatch/types/typesModule'
import {getDistanceFormat} from 'common/helpersUOM'
import StopSummary from './StopSummary'
import StatusBadge from '../StatusBadge'
import {NavigateFunction} from 'react-router-dom'
import {
  RoutePlanWithColor,
  SegmentWithColor,
} from 'routingAndDispatch/Routes/PlanReview/PlanReview'
import {
  AZURE_IMAGE_STORAGE_ACCOUNT_URL_BASE,
  GRACE_PERIOD_MINUTES_FOR_LATE_CALC,
} from 'common/constants'
import {numberWithCommas} from 'common/helpersNumber'
import {isValidDate} from 'common/helpersDateTime'
import {Tooltip} from 'common/components'
import {AssetImage, AssetImageDiv} from 'assets/AssetList'
import {createRoot} from 'react-dom/client'

const TimeCell = ({
  data,
  plan,
  navigate,
  earliestTime,
  latestTime,
}: {
  data: Segment
  plan: RoutePlanWithColor
  navigate?: NavigateFunction
  earliestTime: Date
  latestTime: Date
}) => {
  const svgRef = React.useRef<SVGSVGElement | null>(null)
  React.useEffect(() => {
    if (data && plan && svgRef)
      drawTimeChart(data, plan, svgRef, earliestTime, latestTime, navigate)
  }, [data, plan, navigate, earliestTime, latestTime])

  return (
    <ChartSvg
      key={`${data.idRouteDispatchDriver}`}
      id={`timecell-${data.idRouteDispatchDriver}`}
      ref={svgRef}
    ></ChartSvg>
  )
}

const TimeHeader = ({
  earliestTime,
  latestTime,
}: {
  earliestTime: Date
  latestTime: Date
}) => {
  const svgRef = React.useRef<SVGSVGElement | null>(null)

  React.useEffect(() => {
    drawTimeHeader({svgRef, earliestTime, latestTime})
  }, [earliestTime, latestTime])
  return <svg id={`timeheader`} viewBox="0 0 2400 26" ref={svgRef}></svg>
}

//exporting for test
// eslint-disable-next-line import/no-unused-modules
export const getDurationString = (stop: StopType) => {
  const durationInMs =
    stop.ServiceStartDateTime && stop.ServiceEndDateTime
      ? stop.ServiceEndDateTime.getTime() - stop.ServiceStartDateTime.getTime()
      : 0
  const durationInMinutes = durationInMs / (1000 * 60)

  const roundedDurationInMinutes = Math.round(durationInMinutes)

  const durationInHours = Math.floor(roundedDurationInMinutes / 60)
  const durationInMinutesRemainder = roundedDurationInMinutes % 60

  let durationString = ''

  if (durationInHours > 0) {
    durationString += `${durationInHours}hr `
  }

  if (durationInMinutesRemainder > 0) {
    durationString += `${durationInMinutesRemainder}m`
  } else if (durationInHours <= 0 && durationInMinutesRemainder === 0) {
    durationString += `${Math.round(durationInMinutes)}m`
  }
  return durationString
}
//exported in StopSummary.tsx, StopPointCard.tsx
// eslint-disable-next-line import/no-unused-modules
export const getIsStopLate = (stop: StopType) => {
  if (stop.isLate) {
    return stop.isLate
  } else {
    if (!stop.ServiceStartDateTime) return false
    const dateTimeArrivalDate =
      stop.ArrivalDate && stop.ArrivalDate !== '0001-01-01T00:00:00'
        ? new Date(stop.ArrivalDate)
        : ''
    const lateDateTime = addMinutes(
      stop.ServiceStartDateTime,
      GRACE_PERIOD_MINUTES_FOR_LATE_CALC,
    )
    if (dateTimeArrivalDate) {
      const arrivedLate = dateTimeArrivalDate.getTime() > lateDateTime.getTime()
      return arrivedLate
    } else {
      const neverArrivedAndLate = new Date().getTime() > lateDateTime.getTime()

      return neverArrivedAndLate
    }
  }
}

export interface ValuesType extends StopType {
  displayDuration: string
  backgroundColor: string
  borderColor: string
  textColor: string
  icon: string
  status: string
  orderName: string
  idOrder: string
}
const MINIMUM_HOUR_RANGE = 5
const getBarColor = (stop: StopType) => {
  const isLate = getIsStopLate(stop)
  if (!isLate) {
    switch (stop.stopStatus) {
      case 'Missed':
        return {
          status: 'Missed',
          backgroundColor: 'var(--asc-vermilion)',
          borderColor: 'var(--asc-vermilion)',
          textColor: '#fff',
          icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMyAxMyI+PHBhdGggZD0iTTYuNSwwYTYuNTA2ODUsNi41MDY4NSwwLDEsMCw0LjU5NDIsMS45MDU4MkE2LjUsNi41LDAsMCwwLDYuNSwwWk02LDMuNWEuNS41LDAsMSwxLDEsMFY3QS41LjUsMCwwLDEsNiw3Wk02LjUsMTBhLjc1MDA1Ljc1MDA1LDAsMSwxLC41MzAzMy0uMjE5N0EuNzUwMDYuNzUwMDYsMCwwLDEsNi41LDEwWiIgc3R5bGU9ImZpbGw6I2ZmZiIvPjwvc3ZnPg==',
        }
      case 'Completed':
        return {
          status: 'Completed',
          backgroundColor: 'var(--asc-selectedgreen)',
          borderColor: 'var(--asc-lime)',
          textColor: 'var(--asc-moss)',
          icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgZmlsbD0iIzNENDkxRiIgdmlld0JveD0iMCAwIDI1NiAyNTYiPjxyZWN0IHdpZHRoPSIyNTYiIGhlaWdodD0iMjU2IiBmaWxsPSJub25lIj48L3JlY3Q+PHBhdGggZD0iTTEyOCwyNEExMDQsMTA0LDAsMSwwLDIzMiwxMjgsMTA0LjIsMTA0LjIsMCwwLDAsMTI4LDI0Wm00OS41LDg1LjgtNTguNiw1NmE4LjEsOC4xLDAsMCwxLTUuNiwyLjIsNy43LDcuNywwLDAsMS01LjUtMi4ybC0yOS4zLTI4YTgsOCwwLDEsMSwxMS0xMS42bDIzLjgsMjIuNyw1My4yLTUwLjdhOCw4LDAsMCwxLDExLDExLjZaIj48L3BhdGg+PC9zdmc+',
        }
      case 'Arrived':
        return {
          status: 'Arrived',
          backgroundColor: 'var(--asc-lime)',
          borderColor: 'var(--asc-moss)',
          textColor: '#000',
          icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgZmlsbD0iIzAwMDAwMCIgdmlld0JveD0iMCAwIDI1NiAyNTYiPjxwYXRoIGQ9Ik0xMjgsMjRBMTA0LDEwNCwwLDEsMCwyMzIsMTI4LDEwNC4xMSwxMDQuMTEsMCwwLDAsMTI4LDI0Wm0wLDE5MmE4OCw4OCwwLDEsMSw4OC04OEE4OC4xLDg4LjEsMCwwLDEsMTI4LDIxNlptNjQtODhhOCw4LDAsMCwxLTgsOEgxMjhhOCw4LDAsMCwxLTgtOFY3MmE4LDgsMCwwLDEsMTYsMHY0OGg0OEE4LDgsMCwwLDEsMTkyLDEyOFoiPjwvcGF0aD48L3N2Zz4=',
        }
      case 'InTransit':
        return {
          status: 'InTransit',
          backgroundColor: 'var(--asc-gold)',
          borderColor: 'var(--asc-gold)',
          textColor: '#000',
          icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgZmlsbD0iIzAwMDAwMCIgdmlld0JveD0iMCAwIDI1NiAyNTYiPjxwYXRoIGQ9Ik0xMjgsMjRBMTA0LDEwNCwwLDEsMCwyMzIsMTI4LDEwNC4xMSwxMDQuMTEsMCwwLDAsMTI4LDI0Wm0wLDE5MmE4OCw4OCwwLDEsMSw4OC04OEE4OC4xLDg4LjEsMCwwLDEsMTI4LDIxNlptNjQtODhhOCw4LDAsMCwxLTgsOEgxMjhhOCw4LDAsMCwxLTgtOFY3MmE4LDgsMCwwLDEsMTYsMHY0OGg0OEE4LDgsMCwwLDEsMTkyLDEyOFoiPjwvcGF0aD48L3N2Zz4=',
        }
      default:
        return {
          status: 'Assigned',
          backgroundColor: 'var(--asc-platinum)',
          borderColor: 'var(--asc-coolgray',
          textColor: 'var(--asc-jet)',
          icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgZmlsbD0iIzAwMDAwMCIgdmlld0JveD0iMCAwIDI1NiAyNTYiPjxwYXRoIGQ9Ik0xMjgsMjRBMTA0LDEwNCwwLDEsMCwyMzIsMTI4LDEwNC4xMSwxMDQuMTEsMCwwLDAsMTI4LDI0Wm0wLDE5MmE4OCw4OCwwLDEsMSw4OC04OEE4OC4xLDg4LjEsMCwwLDEsMTI4LDIxNlptNjQtODhhOCw4LDAsMCwxLTgsOEgxMjhhOCw4LDAsMCwxLTgtOFY3MmE4LDgsMCwwLDEsMTYsMHY0OGg0OEE4LDgsMCwwLDEsMTkyLDEyOFoiPjwvcGF0aD48L3N2Zz4=',
        }
    }
  }
  if (isLate) {
    if (stop.stopStatus === 'Arrived') {
      return {
        status: 'Late',
        backgroundColor: 'var(--asc-lime)',
        borderColor: 'var(--asc-moss)',
        textColor: '#fff',
        icon: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iOSIgY3k9IjkiIHI9IjkiIGZpbGw9IndoaXRlIi8+CjxnIGNsaXAtcGF0aD0idXJsKCNjbGlwMF8yMDk5XzI4MTM1KSI+CjxwYXRoIGQ9Ik05IDIuNUM3LjcxNDQyIDIuNSA2LjQ1NzcyIDIuODgxMjIgNS4zODg4IDMuNTk1NDVDNC4zMTk4OCA0LjMwOTY4IDMuNDg2NzYgNS4zMjQ4NCAyLjk5NDc5IDYuNTEyNTZDMi41MDI4MiA3LjcwMDI4IDIuMzc0MDkgOS4wMDcyMSAyLjYyNDkgMTAuMjY4MUMyLjg3NTcgMTEuNTI5IDMuNDk0NzcgMTIuNjg3MiA0LjQwMzgxIDEzLjU5NjJDNS4zMTI4NSAxNC41MDUyIDYuNDcxMDQgMTUuMTI0MyA3LjczMTkyIDE1LjM3NTFDOC45OTI3OSAxNS42MjU5IDEwLjI5OTcgMTUuNDk3MiAxMS40ODc0IDE1LjAwNTJDMTIuNjc1MiAxNC41MTMyIDEzLjY5MDMgMTMuNjgwMSAxNC40MDQ2IDEyLjYxMTJDMTUuMTE4OCAxMS41NDIzIDE1LjUgMTAuMjg1NiAxNS41IDlDMTUuNDk4MiA3LjI3NjY1IDE0LjgxMjggNS42MjQ0MSAxMy41OTQyIDQuNDA1ODJDMTIuMzc1NiAzLjE4NzIyIDEwLjcyMzQgMi41MDE4MiA5IDIuNVpNOC41IDZDOC41IDUuODY3MzkgOC41NTI2OCA1Ljc0MDIxIDguNjQ2NDUgNS42NDY0NUM4Ljc0MDIyIDUuNTUyNjggOC44NjczOSA1LjUgOSA1LjVDOS4xMzI2MSA1LjUgOS4yNTk3OSA1LjU1MjY4IDkuMzUzNTYgNS42NDY0NUM5LjQ0NzMyIDUuNzQwMjEgOS41IDUuODY3MzkgOS41IDZWOS41QzkuNSA5LjYzMjYxIDkuNDQ3MzIgOS43NTk3OSA5LjM1MzU2IDkuODUzNTVDOS4yNTk3OSA5Ljk0NzMyIDkuMTMyNjEgMTAgOSAxMEM4Ljg2NzM5IDEwIDguNzQwMjIgOS45NDczMiA4LjY0NjQ1IDkuODUzNTVDOC41NTI2OCA5Ljc1OTc5IDguNSA5LjYzMjYxIDguNSA5LjVWNlpNOSAxMi41QzguODUxNjcgMTIuNSA4LjcwNjY2IDEyLjQ1NiA4LjU4MzMzIDEyLjM3MzZDOC40NTk5OSAxMi4yOTEyIDguMzYzODYgMTIuMTc0MSA4LjMwNzA5IDEyLjAzN0M4LjI1MDMzIDExLjkgOC4yMzU0OCAxMS43NDkyIDguMjY0NDEgMTEuNjAzN0M4LjI5MzM1IDExLjQ1ODIgOC4zNjQ3OCAxMS4zMjQ2IDguNDY5NjcgMTEuMjE5N0M4LjU3NDU2IDExLjExNDggOC43MDgyIDExLjA0MzQgOC44NTM2OCAxMS4wMTQ0QzguOTk5MTcgMTAuOTg1NSA5LjE0OTk3IDExLjAwMDMgOS4yODcwMSAxMS4wNTcxQzkuNDI0MDYgMTEuMTEzOSA5LjU0MTE5IDExLjIxIDkuNjIzNiAxMS4zMzMzQzkuNzA2MDIgMTEuNDU2NyA5Ljc1IDExLjYwMTcgOS43NSAxMS43NUM5Ljc1IDExLjk0ODkgOS42NzA5OCAxMi4xMzk3IDkuNTMwMzMgMTIuMjgwM0M5LjM4OTY4IDEyLjQyMSA5LjE5ODkyIDEyLjUgOSAxMi41WiIgZmlsbD0iI0QyNDM0MSIvPgo8L2c+CjxkZWZzPgo8Y2xpcFBhdGggaWQ9ImNsaXAwXzIwOTlfMjgxMzUiPgo8cmVjdCB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIGZpbGw9IndoaXRlIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgxIDEpIi8+CjwvY2xpcFBhdGg+CjwvZGVmcz4KPC9zdmc+Cg==',
      }
    }
    if (stop.stopStatus === 'InTransit') {
      return {
        status: 'Late',
        backgroundColor: 'var(--asc-stone)',
        borderColor: 'var(--asc-tan)',
        textColor: 'black',
        icon: 'data:image/svg+xml;base64,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',
      }
    }
    if (stop.stopStatus === 'New') {
      return {
        status: 'Late',
        backgroundColor: 'var(--asc-platinum)',
        borderColor: 'var(--asc-vermilion)',
        textColor: 'var(--asc-jet)',
        icon: 'data:image/svg+xml;base64,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',
      }
    }
    if (stop.stopStatus === 'Missed') {
      return {
        status: 'Missed',
        backgroundColor: 'var(--asc-vermilion)',
        borderColor: 'var(--asc-vermilion)',
        textColor: 'white',
        icon: 'data:image/svg+xml;base64,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',
      }
    }
    if (stop.stopStatus === 'Completed') {
      return {
        status: 'Completed Late',
        backgroundColor: 'var(--asc-selectedgreen)',
        borderColor: 'var(--asc-moss)',
        textColor: 'var(--asc-moss)',
        icon: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iOSIgY3k9IjkiIHI9IjkiIGZpbGw9IndoaXRlIi8+CjxnIGNsaXAtcGF0aD0idXJsKCNjbGlwMF8yMDk5XzI4MTM1KSI+CjxwYXRoIGQ9Ik05IDIuNUM3LjcxNDQyIDIuNSA2LjQ1NzcyIDIuODgxMjIgNS4zODg4IDMuNTk1NDVDNC4zMTk4OCA0LjMwOTY4IDMuNDg2NzYgNS4zMjQ4NCAyLjk5NDc5IDYuNTEyNTZDMi41MDI4MiA3LjcwMDI4IDIuMzc0MDkgOS4wMDcyMSAyLjYyNDkgMTAuMjY4MUMyLjg3NTcgMTEuNTI5IDMuNDk0NzcgMTIuNjg3MiA0LjQwMzgxIDEzLjU5NjJDNS4zMTI4NSAxNC41MDUyIDYuNDcxMDQgMTUuMTI0MyA3LjczMTkyIDE1LjM3NTFDOC45OTI3OSAxNS42MjU5IDEwLjI5OTcgMTUuNDk3MiAxMS40ODc0IDE1LjAwNTJDMTIuNjc1MiAxNC41MTMyIDEzLjY5MDMgMTMuNjgwMSAxNC40MDQ2IDEyLjYxMTJDMTUuMTE4OCAxMS41NDIzIDE1LjUgMTAuMjg1NiAxNS41IDlDMTUuNDk4MiA3LjI3NjY1IDE0LjgxMjggNS42MjQ0MSAxMy41OTQyIDQuNDA1ODJDMTIuMzc1NiAzLjE4NzIyIDEwLjcyMzQgMi41MDE4MiA5IDIuNVpNOC41IDZDOC41IDUuODY3MzkgOC41NTI2OCA1Ljc0MDIxIDguNjQ2NDUgNS42NDY0NUM4Ljc0MDIyIDUuNTUyNjggOC44NjczOSA1LjUgOSA1LjVDOS4xMzI2MSA1LjUgOS4yNTk3OSA1LjU1MjY4IDkuMzUzNTYgNS42NDY0NUM5LjQ0NzMyIDUuNzQwMjEgOS41IDUuODY3MzkgOS41IDZWOS41QzkuNSA5LjYzMjYxIDkuNDQ3MzIgOS43NTk3OSA5LjM1MzU2IDkuODUzNTVDOS4yNTk3OSA5Ljk0NzMyIDkuMTMyNjEgMTAgOSAxMEM4Ljg2NzM5IDEwIDguNzQwMjIgOS45NDczMiA4LjY0NjQ1IDkuODUzNTVDOC41NTI2OCA5Ljc1OTc5IDguNSA5LjYzMjYxIDguNSA5LjVWNlpNOSAxMi41QzguODUxNjcgMTIuNSA4LjcwNjY2IDEyLjQ1NiA4LjU4MzMzIDEyLjM3MzZDOC40NTk5OSAxMi4yOTEyIDguMzYzODYgMTIuMTc0MSA4LjMwNzA5IDEyLjAzN0M4LjI1MDMzIDExLjkgOC4yMzU0OCAxMS43NDkyIDguMjY0NDEgMTEuNjAzN0M4LjI5MzM1IDExLjQ1ODIgOC4zNjQ3OCAxMS4zMjQ2IDguNDY5NjcgMTEuMjE5N0M4LjU3NDU2IDExLjExNDggOC43MDgyIDExLjA0MzQgOC44NTM2OCAxMS4wMTQ0QzguOTk5MTcgMTAuOTg1NSA5LjE0OTk3IDExLjAwMDMgOS4yODcwMSAxMS4wNTcxQzkuNDI0MDYgMTEuMTEzOSA5LjU0MTE5IDExLjIxIDkuNjIzNiAxMS4zMzMzQzkuNzA2MDIgMTEuNDU2NyA5Ljc1IDExLjYwMTcgOS43NSAxMS43NUM5Ljc1IDExLjk0ODkgOS42NzA5OCAxMi4xMzk3IDkuNTMwMzMgMTIuMjgwM0M5LjM4OTY4IDEyLjQyMSA5LjE5ODkyIDEyLjUgOSAxMi41WiIgZmlsbD0iI0QyNDM0MSIvPgo8L2c+CjxkZWZzPgo8Y2xpcFBhdGggaWQ9ImNsaXAwXzIwOTlfMjgxMzUiPgo8cmVjdCB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIGZpbGw9IndoaXRlIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgxIDEpIi8+CjwvY2xpcFBhdGg+CjwvZGVmcz4KPC9zdmc+Cg==',
      }
    }
  }
  // below just to fix angry TS
  return {
    status: '',
    backgroundColor: 'var(--asc-platinum)',
    borderColor: 'var(--asc-coolgray',
    textColor: 'var(--asc-jet)',
    icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgZmlsbD0iIzAwMDAwMCIgdmlld0JveD0iMCAwIDI1NiAyNTYiPjxwYXRoIGQ9Ik0xMjgsMjRBMTA0LDEwNCwwLDEsMCwyMzIsMTI4LDEwNC4xMSwxMDQuMTEsMCwwLDAsMTI4LDI0Wm0wLDE5MmE4OCw4OCwwLDEsMSw4OC04OEE4OC4xLDg4LjEsMCwwLDEsMTI4LDIxNlptNjQtODhhOCw4LDAsMCwxLTgsOEgxMjhhOCw4LDAsMCwxLTgtOFY3MmE4LDgsMCwwLDEsMTYsMHY0OGg0OEE4LDgsMCwwLDEsMTkyLDEyOFoiPjwvcGF0aD48L3N2Zz4=',
  }
}

const BAR_HEIGHT = 30
const FONT_SIZE = '0.70em'
const margin = {top: 0, right: 0, bottom: 0, left: 0},
  padding = {top: 0, right: 0, bottom: 0, left: 0},
  outerWidth = 2400,
  outerHeight = BAR_HEIGHT + 10,
  innerWidth = outerWidth - margin.left - margin.right,
  width = innerWidth - padding.left - padding.right

const drawTimeHeader = ({
  svgRef,
  earliestTime,
  latestTime,
}: {
  svgRef: React.RefObject<SVGElement | null>
  earliestTime: Date
  latestTime: Date
}) => {
  const startDate = earliestTime
  const endDate = latestTime

  const hoursDifference =
    differenceInHours(endDate, startDate) > MINIMUM_HOUR_RANGE
      ? differenceInHours(endDate, startDate)
      : MINIMUM_HOUR_RANGE
  var timeScale = scaleTime()
    .domain([addMinutes(startDate, -30), addMinutes(endDate, 30)])
    .range([0, width])
  // .nice()
  //header in table
  const userConfig = useStore.getState().userConfig
  const header_FONT_SIZE = '1em'
  const headerSvg = select(svgRef.current)
  function make_header() {
    return axisBottom(timeScale)
      .ticks(hoursDifference)
      .tickFormat(
        (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          d: any, // did this because it doesn't like it when I type it because the tickFormat returns null
        ) => {
          return timeFormat(
            userConfig && userConfig.Time_Format === '12HR' ? '%I %p' : '%H:%M',
          )(d).replace(/^0/, '')
        },
      )
      .tickSize(0)
  }
  headerSvg.selectAll('g').remove()

  headerSvg
    .append('g')
    .style('font-size', header_FONT_SIZE)
    .attr('color', 'white')
    .style('font-family', 'Open Sans')
    .call(make_header())
    .attr('transform', () => `translate(0,4)`)

  headerSvg.selectAll('.tick').attr('role', 'timeValue')
  headerSvg.selectAll('.tick line').attr('opacity', 0)
  headerSvg.selectAll('.domain').attr('opacity', 0)

  //Time now line
  const nowTime = new Date()

  headerSvg
    .append('line')
    .attr('x1', () => timeScale(nowTime))
    .attr('y1', 0)
    .attr('x2', () => timeScale(nowTime))
    .attr('y2', outerHeight + 25)
    .attr('stroke', 'transparent')
    .attr('stroke-width', 2)
    .attr('id', 'time-now-line')
    .text(`Now`)

  const updateNowTimeLine = () => {
    const nowTime = new Date()

    // Update the position of the time line
    headerSvg
      .select('#time-now-line')
      .attr('x1', () => timeScale(nowTime))
      .attr('x2', () => timeScale(nowTime))
      .text(`Now`)
  }

  // Initial drawing of the time line
  updateNowTimeLine()

  // Update the time line every minute
  setInterval(updateNowTimeLine, 60000)
}

const drawTimeChart = (
  segment: Segment,
  plan: RoutePlanWithColor,
  svgRef: React.MutableRefObject<SVGSVGElement | null>,
  earliestTime: Date,
  latestTime: Date,
  navigate?: NavigateFunction,
) => {
  const segmentWithEndpoints = plan?.segments?.find(testSegment => {
    return segment.idRouteDispatchDriver === testSegment.idRouteDispatchDriver
  })
  const startDate = earliestTime
  const endDate = latestTime

  var timeScale = scaleTime()
    .domain([addMinutes(startDate, -30), addMinutes(endDate, 30)])
    .range([0, width])
  // .nice()
  const hoursDifference =
    differenceInHours(endDate, startDate) > MINIMUM_HOUR_RANGE
      ? differenceInHours(endDate, startDate)
      : MINIMUM_HOUR_RANGE

  const svg = select(svgRef.current).attr(
    'viewBox',
    `0 0 ${outerWidth} ${outerHeight}`,
  )
  svg.selectAll('g').remove()
  const g = svg.append('g')

  const values: ValuesType[] = []

  const isAnEndpoint = (d: StopType) => {
    return d.stopType === 'origin' || d.stopType === 'destination'
  }

  if (
    segmentWithEndpoints &&
    segmentWithEndpoints?.stops.length &&
    segmentWithEndpoints?.stops?.length > 0
  ) {
    segmentWithEndpoints?.stops?.forEach((stop: StopType) => {
      const durationString = getDurationString(stop)
      const {backgroundColor, borderColor, textColor, icon, status} =
        getBarColor(stop)
      values.push({
        ...stop,
        displayDuration: durationString,
        backgroundColor,
        borderColor,
        textColor,
        icon,
        status,
        orderName: stop.orderName || '',
        idOrder: stop.idOrder || '',
      })
    })
  }

  // gridlines in x axis function
  function make_x_gridlines() {
    //hides the axis lines and text
    const gridlines = axisBottom(timeScale)
      .ticks(hoursDifference)
      .tickSize(100)
      .tickFormat(null)
    return gridlines
  }

  //horizontal backgound bars
  g.selectAll('rect')
    // .data(segment || [])
    .enter()
    .append('rect')
    .attr('className', 'bgBars')
    .attr('height', outerHeight)
    .attr('width', width)
    .attr('x', 0)
    .attr('fill', 'white')

  g.append('g')
    .attr('class', 'grid')
    .attr('color', 'var(--asc-coolgray)')
    .call(make_x_gridlines())
  g.selectAll('.domain').attr('opacity', 0)

  //stop bars
  const bars = g
    .selectAll('.bar')
    .data(values)
    .enter()
    .append('g')
    .attr('data-cy', 'stop-bar-parent')
    .attr('role', 'stop-bar-parent')

  //Start/End route icons
  // Append the background rectangle
  bars
    .filter(d => {
      return isAnEndpoint(d)
    })
    .append('rect')
    .attr('x', d =>
      d.stopType === 'origin'
        ? timeScale(d.ServiceEndDateTime || 0) - 34
        : timeScale(d.ServiceEndDateTime || 0),
    )
    .attr('y', 0)
    .attr('width', 36)
    .attr('height', '100%')
    .attr('fill', d => d.backgroundColor)
  // Append the left vertical line
  bars
    .filter(d => {
      return isAnEndpoint(d)
    })
    .append('rect')
    .attr('x', d =>
      d.stopType === 'origin'
        ? timeScale(d.ServiceEndDateTime || 0) - 34
        : timeScale(d.ServiceEndDateTime || 0),
    )
    .attr('y', 0)
    .attr('width', 1)
    .attr('height', '100%')
    .attr('fill', d => d.borderColor)

  // Append the right vertical line
  bars
    .filter(d => {
      return isAnEndpoint(d)
    })
    .append('rect')
    .attr('x', d =>
      d.stopType === 'origin'
        ? timeScale(d.ServiceEndDateTime || 0) + 1
        : timeScale(d.ServiceEndDateTime || 0) + 35,
    )
    .attr('y', 0)
    .attr('width', 1)
    .attr('height', '100%')
    .attr('fill', d => d.borderColor)

  bars
    .filter(d => {
      return isAnEndpoint(d)
    })
    .append('svg')
    .attr('x', d =>
      d.stopType === 'origin'
        ? timeScale(d.ServiceEndDateTime || 0) - 26
        : timeScale(d.ServiceEndDateTime || 0) + 8,
    )
    .attr('y', 10)
    .attr('width', 20)
    .attr('data-cy', 'endpoint-icon')
    .each(function (d) {
      const svgContainer = select(this)
      const iconColor = d.status.toLowerCase().includes('late')
        ? 'var(--asc-vermilion)'
        : d.stopType === 'origin' &&
          (d.status === '' ||
            d.status === 'new' ||
            d.status.toLowerCase() === 'assigned')
        ? 'var(--asc-graphite)'
        : d.textColor
      if (d.stopType === 'origin') {
        const houseSvgString = ReactDOMServer.renderToString(
          <House size={20} color={iconColor} weight="fill" />,
        )
        svgContainer.html(houseSvgString)
      } else {
        const flagSvgString = ReactDOMServer.renderToString(
          <FlagCheckered size={20} color={iconColor} weight="fill" />,
        )
        svgContainer.html(flagSvgString)
      }
    })

  bars
    .filter(d => {
      return !isAnEndpoint(d)
    })
    .append('rect')
    .attr('class', 'bar')
    .attr('x', d => timeScale(d.ServiceStartDateTime || 0))
    .attr('height', BAR_HEIGHT)
    .attr('width', d => {
      const start = timeScale(d.ServiceStartDateTime || 0)
      const end = timeScale(d.ServiceEndDateTime || 0)
      return end - start > 5 ? end - start : 5
    })
    .attr('stroke', d => d.borderColor)
    .attr('stroke-width', 1)
    .attr('fill', d => d.backgroundColor)
    .attr('rx', 5)
    .attr('transform', () => `translate(0,5)`)
    .attr('data-cy', 'stop-bar')
    .attr('role', 'stop-bar')
    .style('cursor', 'pointer')

  //clock icon on stop bar
  bars
    .filter(d => {
      return !isAnEndpoint(d)
    })
    .append('image')
    .attr('x', d =>
      d.displayDuration.includes('hr') && d.displayDuration.includes('m')
        ? timeScale(d.ServiceEndDateTime || 0) - 70
        : timeScale(d.ServiceEndDateTime || 0) - 45,
    )
    .attr('y', 12)
    .attr('width', d => {
      const start = timeScale(d.ServiceStartDateTime || 0)
      const end = timeScale(d.ServiceEndDateTime || 0)
      return end - start < 70 ? 0 : 16
    })
    .attr('height', 16)
    .attr('fill', d => d.textColor)
    .style('cursor', 'pointer')
    .attr('xlink:href', d => {
      const start = timeScale(d.ServiceStartDateTime || 0)
      const end = timeScale(d.ServiceEndDateTime || 0)
      return end - start > 70 ? d.icon : ''
    })

  //duration text on stop bar
  bars
    .filter(d => {
      return !isAnEndpoint(d)
    })
    .append('text')
    .attr('x', d => {
      return d.displayDuration.includes('hr') && d.displayDuration.includes('m')
        ? timeScale(d.ServiceEndDateTime || 0) - 50
        : timeScale(d.ServiceEndDateTime || 0) - 26
    })
    .attr('y', BAR_HEIGHT / 2 + 1)
    .attr('dy', '7px')
    .attr('fill', d => d.textColor)
    .style('font-size', FONT_SIZE)
    .style('cursor', 'pointer')
    .attr('role', 'duration-text')
    .html(d => {
      const matches = d.displayDuration.match(/\d+/g)
      if (matches && matches.length > 0) {
        let duration = d.displayDuration

        for (const match of matches) {
          duration = duration.replace(
            match,
            `<tspan font-weight="bold">${match}</tspan> `,
          )
        }
        if (
          d.displayDuration.includes('m') &&
          !d.displayDuration.includes('hr') &&
          parseInt(matches[0]) < 15
        ) {
          duration = ''
        }
        return duration
      }
      return d.displayDuration
    })

  //stop index on stop bar
  bars
    .filter(d => {
      return !isAnEndpoint(d)
    })
    .append('text')
    .attr('x', d => {
      const start = timeScale(d.ServiceStartDateTime || 0)
      const end = timeScale(d.ServiceEndDateTime || 0)
      return end - start >= 5 ? timeScale(d.ServiceStartDateTime || 0) + 5 : ''
    })
    .attr('y', BAR_HEIGHT / 2 + 1)
    .attr('dy', '7px')
    .style('font-size', FONT_SIZE)
    .style('cursor', 'pointer')
    .attr('fill', d => d.textColor)
    .text((d, index) => {
      const start = timeScale(d.ServiceStartDateTime || 0)
      const end = timeScale(d.ServiceEndDateTime || 0)
      return end - start >= 5 ? index + 1 : ''
    })

  // add a mouseover event listener to each bar
  bars
    .on('mouseover', function (e, d) {
      select(this).select('rect').attr('fill', 'var(--primary)')
      select(this).selectAll('text').attr('fill', 'var(--secondary)')

      select(this)
        .select('image')
        .attr('xlink:href', () => {
          const start = timeScale(d.ServiceStartDateTime || 0)
          const end = timeScale(d.ServiceEndDateTime || 0)
          return end - start > 70
            ? 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgZmlsbD0iI2I5Y2YzMyIgdmlld0JveD0iMCAwIDI1NiAyNTYiPjxwYXRoIGQ9Ik0xMjgsMjRBMTA0LDEwNCwwLDEsMCwyMzIsMTI4LDEwNC4xMSwxMDQuMTEsMCwwLDAsMTI4LDI0Wm0wLDE5MmE4OCw4OCwwLDEsMSw4OC04OEE4OC4xLDg4LjEsMCwwLDEsMTI4LDIxNlptNjQtODhhOCw4LDAsMCwxLTgsOEgxMjhhOCw4LDAsMCwxLTgtOFY3MmE4LDgsMCwwLDEsMTYsMHY0OGg0OEE4LDgsMCwwLDEsMTkyLDEyOFoiPjwvcGF0aD48L3N2Zz4='
            : ''
        })
      //remove any tooltips
      const currentTooltips = document.getElementsByClassName(`tooltip`)
      if (currentTooltips.length > 0) {
        while (currentTooltips[0] && currentTooltips[0].parentNode) {
          currentTooltips[0].parentNode.removeChild(currentTooltips[0])
        }
      }
      // create a new div element with the desired content
      const infoDiv = document.createElement('div')

      infoDiv.id = `tooltip-${segment.idRouteDispatchDriver}`
      infoDiv.className = `tooltip`

      const infoDivRoot = createRoot(infoDiv)
      infoDivRoot.render(<StopSummary d={d} />)

      // position the div element relative to the bar
      const rect = this.getBoundingClientRect()
      const barCenter = rect.left + rect.width / 2 + window.scrollX
      let top = rect.top + window.scrollY + rect.height + 5
      infoDiv.style.position = 'absolute'

      const tooltipWidth = 450
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight
      // Calculate the initial left position for centering the div on the bar
      let left = barCenter - tooltipWidth / 2

      // Check if the tooltip overflows on the right side of the screen
      if (left + tooltipWidth > screenWidth) {
        // If the tooltip overflows, align it with the right side of the rect
        left = rect.right - tooltipWidth

        if (left > screenWidth) {
          left = rect.left
        }
      }

      if (top + 350 > screenHeight) {
        top = rect.top - 320
      }
      if (left < 0) {
        left = rect.left
      }
      infoDiv.style.left = `${left}px`
      infoDiv.style.top = `${top}px`

      // append the div element to the document
      document.body.appendChild(infoDiv)
    })
    .on('mouseout', function (e, d) {
      select(this).select('rect').attr('fill', d.backgroundColor)
      select(this).selectAll('text').attr('fill', d.textColor)
      select(this)
        .select('image') // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .attr('xlink:href', (d: any) => {
          // did this because it doesn't like it when I type it because the tickFormat returns null
          const start = timeScale(d.ServiceStartDateTime)
          const end = timeScale(d.ServiceEndDateTime)
          return end - start > 70 ? d.icon : ''
        })
      const infoDiv = document.getElementById(
        `tooltip-${segment.idRouteDispatchDriver}`,
      )
      if (infoDiv) {
        infoDiv.remove()
      }
    })

  bars.on('click', function (e, d) {
    const currentTooltips = document.getElementsByClassName(`tooltip`)
    if (currentTooltips.length > 0) {
      while (currentTooltips[0] && currentTooltips[0].parentNode) {
        currentTooltips[0].parentNode.removeChild(currentTooltips[0])
      }
    }
    const passSegment = {
      planStartDateTime: plan.planStartDateTime,
      planEndDateTime: plan.planStartDateTime,
      planName: plan.planName,
      planStatus: plan.planStatus,
      idPlan: plan.idPlan,
      segments: plan?.segments
        ? [
            {
              ...segment,
              stops: plan?.segments.filter(
                testSegment =>
                  testSegment.idRouteDispatchDriver ===
                  segment.idRouteDispatchDriver,
              )[0].stops,
            },
          ]
        : [segment],
    }
    const scrollToIndex = passSegment.segments[0].stops.findIndex(
      stop =>
        stop.ServiceStartDateTime?.getTime() ===
        d.ServiceStartDateTime?.getTime(),
    )
    if (navigate)
      navigate(
        `/routing/routes/${plan.idPlan}/${segment.idRouteDispatchDriver}`,
        {
          state: {route: passSegment, scrollToIndex},
        },
      )
  })
}

//didn't want to break down the map object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createMapOptions(maps: any) {
  // next props are exposed at maps
  // "Animation", "ControlPosition", "MapTypeControlStyle", "MapTypeId",
  // "NavigationControlStyle", "ScaleControlStyle", "StrokePosition", "SymbolPath", "ZoomControlStyle",
  // "DirectionsStatus", "DirectionsTravelMode", "DirectionsUnitSystem", "DistanceMatrixStatus",
  // "DistanceMatrixElementStatus", "ElevationStatus", "GeocoderLocationType", "GeocoderStatus", "KmlLayerStatus",
  // "MaxZoomStatus", "StreetViewStatus", "TransitMode", "TransitRoutePreference", "TravelMode", "UnitSystem"
  return {
    mapTypeControlOptions: {
      style: maps.MapTypeControlStyle.DEFAULT,
      position: maps.ControlPosition.BOTTOM_RIGHT,
      mapTypeIds: [
        maps.MapTypeId.ROADMAP,
        maps.MapTypeId.SATELLITE,
        maps.MapTypeId.HYBRID,
      ],
    },

    tilt: 0,
    rotateControl: false,
    mapTypeControl: true,
    zoomControlOptions: {
      position: maps.ControlPosition.RIGHT_BOTTOM,
    },
    streetViewControl: true,
    streetViewControlOptions: {
      position: maps.ControlPosition.RIGHT_BOTTOM,
    },
    minZoom: 5,
    // fullscreenControl: true,
    fullscreenControlOptions: {
      position: maps.ControlPosition.RIGHT_BOTTOM,
    },
  }
}

export const getDateOptions = (
  userConfig: UserConfig | null,
  dates?: Date[],
) => {
  const options: {
    key: string
    text: string
    value: string
    disabled?: boolean
  }[] = []

  const firstDate = dates && dates[0]
  const lastDate = dates && dates[dates.length - 1]
  let unusedDates: Date[] | undefined =
    firstDate &&
    lastDate &&
    eachDayOfInterval({start: firstDate, end: lastDate})

  if (firstDate && lastDate && unusedDates) {
    unusedDates.forEach(testDate => {
      if (dates.find(day => isSameDay(startOfDay(day), testDate))) {
        unusedDates = unusedDates?.filter(date => date !== testDate)
      }
    })
  }

  dates?.forEach(date => {
    if (isValidDate(date)) {
      const stringFormat = format(
        startOfDay(date),
        userConfig?.Date_Format || 'MM/dd/yyyy',
      )
      options.push({key: stringFormat, text: stringFormat, value: stringFormat})
    }
  })

  if (unusedDates && unusedDates?.length > 0) {
    unusedDates?.forEach(date => {
      const stringFormat = format(
        startOfDay(date),
        userConfig?.Date_Format || 'MM/dd/yyyy',
      )
      options.push({
        key: stringFormat,
        text: stringFormat,
        value: stringFormat,
        disabled: true,
      })
    })
  }

  options.sort(
    (a, b) =>
      parse(
        a.value,
        userConfig?.Date_Format || 'MM/dd/yyyy',
        new Date(),
      ).getTime() -
      parse(
        b.value,
        userConfig?.Date_Format || 'MM/dd/yyyy',
        new Date(),
      ).getTime(),
  )
  return options
}

//used in routetimetableheaders not sure why eslint thinks it isn't used
// eslint-disable-next-line import/no-unused-modules
export const getHeaders = ({
  routeData,
  isRouteDetail,
  earliestTime,
  latestTime,
  selectAllClick,
  selectedSegments,
}: {
  routeData: RoutePlanWithColor
  isRouteDetail: boolean
  earliestTime: Date
  latestTime: Date
  selectAllClick?: () => void
  selectedSegments?: string[]
}) => {
  const commonInfoColumns = [
    {
      accessor: 'checkbox',
      cell: (
        <SelectAllHeaderCell onClick={selectAllClick}>
          {selectedSegments?.length === routeData?.segments.length ? (
            <CheckSquare color="white" weight="bold" size={18} />
          ) : (
            <Square color="white" weight="bold" size={18} />
          )}
        </SelectAllHeaderCell>
      ),
      disableSort: true,
    },
    {
      accessor: 'driverName',
      cell: (
        <TextCell key={`driverName-header`} gridArea="driverName">
          Driver
        </TextCell>
      ),
    },

    {
      accessor: 'Asset?.Asset_Label',
      cell: (
        <TextCell key={`vehicle-header`} gridArea="assetLabel">
          Vehicle
        </TextCell>
      ),
      width: 80,
    },
  ]

  const commonActionColumns = [
    {
      accessor: 'info.totalDistance',
      cell: (
        <TextCell key={`distance-header`} gridArea="distance">
          Distance
        </TextCell>
      ),
    },

    {
      accessor: 'info.totalDuration',
      cell: (
        <TextCell key={`duration-header`} gridArea="duration">
          Duration
        </TextCell>
      ),
    },
    {accessor: 'buttons', cell: <></>, disableSort: true}, //buttons header

    {
      accessor: 'chart',
      cell: (
        <TimeHeader
          key="timetable-header"
          earliestTime={earliestTime}
          latestTime={latestTime}
        />
      ),
      disableSort: true,
    },
  ]

  const planningColumns = [
    {
      accessor: 'stops',
      cell: (
        <TextCell key={`stops-header`} gridArea="totalStops">
          Stops
        </TextCell>
      ),
    },
  ]

  const otherColumns = [
    {
      accessor: 'segmentStatus',
      cell: (
        <TextCell key={`status-header`} gridArea="status">
          Status
        </TextCell>
      ),
    },

    {
      accessor: 'completed', //completed
      cell: (
        <TextCell key={`stops-header`} gridArea="completed">
          Stops
        </TextCell>
      ),
    },
  ]
  return isRouteDetail
    ? [
        {
          accessor: 'chart',
          cell: (
            <TimeHeader
              key="timetable-header"
              earliestTime={earliestTime}
              latestTime={latestTime}
            />
          ),
          disableSort: true,
        },
      ]
    : routeData.planStatus.toLowerCase() === 'planning'
    ? [...commonInfoColumns, ...planningColumns, ...commonActionColumns]
    : [...commonInfoColumns, ...otherColumns, ...commonActionColumns]
}

const RenderTotalDistance = (segment: SegmentWithColor) => {
  const distanceFormat = getDistanceFormat()
  const stringValue = (segment.info.totalDistance || 0).toString()
  const numericValue = stringValue.match(/\d+(\.\d+)?/)
  const nonNumericValue = numericValue
    ? stringValue.replace(numericValue[0], '')
    : ''

  return (
    <TextCell gridArea="distance">
      {numericValue && <strong>{numberWithCommas(numericValue[0])}</strong>}{' '}
      {distanceFormat === 'kilometers'
        ? 'km'
        : distanceFormat === 'miles'
        ? 'mi'
        : ''}
      {nonNumericValue}
    </TextCell>
  )
}

const RenderTotalDuration = (segment: SegmentWithColor) => {
  const isMinutes = segment.info.totalDuration < 60
  const unit = isMinutes ? 'min' : 'hr'
  const displayValue = isMinutes
    ? Math.round(segment.info.totalDuration)
    : (segment.info.totalDuration / 60).toFixed(2)
  return (
    <TextCell gridArea="duration">
      <strong>{displayValue}</strong> {unit}
    </TextCell>
  )
}
//TODO figure out how to switch the switch for more dynamic; tried doing segments[sortBy.accessor] but was having difficulties

const sortRouteTimeTable = ({
  segments,
  sortBy,
}: {
  segments: SegmentWithColor[]
  sortBy: {
    accessor: string
    type: 'asc' | 'des' | ''
  }
}) => {
  let sortedSegments = [...segments]
  switch (sortBy.accessor) {
    case 'driverName': {
      sortedSegments = sortedSegments.sort((a, b) => {
        if (a.driverName && b.driverName) {
          if (sortBy.type === 'asc') {
            return a.driverName.localeCompare(b.driverName, undefined, {
              sensitivity: 'base',
            })
          } else if (sortBy.type === 'des') {
            return b.driverName.localeCompare(a.driverName, undefined, {
              sensitivity: 'base',
            })
          }
        }
        return 0
      })
      break
    }
    case 'Asset?.Asset_Label': {
      sortedSegments = sortedSegments.sort((a, b) => {
        if (a.Asset?.Asset_Label && b.Asset?.Asset_Label) {
          if (sortBy.type === 'asc') {
            return a.Asset?.Asset_Label.localeCompare(
              b.Asset?.Asset_Label,
              undefined,
              {
                sensitivity: 'base',
              },
            )
          } else if (sortBy.type === 'des') {
            return b.Asset?.Asset_Label.localeCompare(
              a.Asset?.Asset_Label,
              undefined,
              {
                sensitivity: 'base',
              },
            )
          }
        }
        return 0
      })
      break
    }
    case 'stops': {
      sortedSegments = sortedSegments.sort((a, b) => {
        if (sortBy.type === 'asc') {
          return a.stops.length - b.stops.length
        } else if (sortBy.type === 'des') {
          return b.stops.length - a.stops.length
        }
        return 0
      })
      break
    }
    case 'info.totalDistance': {
      sortedSegments = sortedSegments.sort((a, b) => {
        if (sortBy.type === 'asc') {
          return a.info.totalDistance - b.info.totalDistance
        } else if (sortBy.type === 'des') {
          return b.info.totalDistance - a.info.totalDistance
        }
        return 0
      })
      break
    }
    case 'info.totalDuration': {
      sortedSegments = sortedSegments.sort((a, b) => {
        if (sortBy.type === 'asc') {
          return a.info.totalDuration - b.info.totalDuration
        } else if (sortBy.type === 'des') {
          return b.info.totalDuration - a.info.totalDuration
        }
        return 0
      })
      break
    }
    case 'segmentStatus': {
      sortedSegments = sortedSegments.sort((a, b) => {
        if (a.segmentStatus && b.segmentStatus) {
          if (sortBy.type === 'asc') {
            return a.segmentStatus.localeCompare(b.segmentStatus, undefined, {
              sensitivity: 'base',
            })
          } else if (sortBy.type === 'des') {
            return b.segmentStatus.localeCompare(a.segmentStatus, undefined, {
              sensitivity: 'base',
            })
          }
        }
        return 0
      })
      break
    }
    case 'completed': {
      sortedSegments = sortedSegments.sort((a, b) => {
        const completedStopsA = a.stops.filter(
          stop =>
            stop.stopStatus && stop.stopStatus.toLowerCase() === 'completed',
        )
        const completedStopsB = b.stops.filter(
          stop =>
            stop.stopStatus && stop.stopStatus.toLowerCase() === 'completed',
        )

        if (sortBy.type === 'asc') {
          return completedStopsA.length - completedStopsB.length
        } else if (sortBy.type === 'des') {
          return completedStopsB.length - completedStopsA.length
        }

        return 0
      })
      break
    }
    default:
      break
  }

  return sortedSegments
}

export const getSegmentRows = ({
  routeData,
  navigate,
  selectedSegments,
  isRouteDetail,
  earliestTime,
  latestTime,
  sortBy,
}: {
  routeData: RoutePlanWithColor
  navigate?: NavigateFunction
  selectedSegments?: string[]
  isRouteDetail: boolean
  earliestTime: Date
  latestTime: Date
  sortBy: {
    accessor: string
    type: 'asc' | 'des' | ''
  }
}) => {
  //string|number|React.JSX.Element
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: any[] = []
  const segments = sortRouteTimeTable({segments: routeData.segments, sortBy})

  segments.forEach(segment => {
    let rowObject = null
    const isSelected =
      selectedSegments?.findIndex(
        row => row === segment.idRouteDispatchDriver,
      ) !== -1
    const actualStops = segment.stops.filter(
      stop => stop.stopType !== 'origin' && stop.stopType !== 'destination',
    )
    const completedStops = actualStops.filter(
      stop => stop.stopStatus && stop.stopStatus.toLowerCase() === 'completed',
    )

    const commonInfoCells = [
      <TextCell
        key={`checkbox-${segment.idRouteDispatchDriver}`}
        gridArea="checkbox"
      >
        {isSelected ? (
          <CheckSquare
            color={segment.color || 'var(--primary)'}
            size={20}
            weight="fill"
          />
        ) : (
          <Square color="var(--asc-coolgray)" size={20} />
        )}
      </TextCell>,
      <TextCell
        key={`driverName-${segment.idRouteDispatchDriver}`}
        gridArea="driverName"
      >
        {segment.driverName}
      </TextCell>,
      <TextCell
        key={`asset-${segment.idRouteDispatchDriver}`}
        gridArea="assetLabel"
      >
        {segment.Asset?.Asset_Label && (
          <Tooltip
            tooltip={
              <div>
                <AssetImageDiv>
                  {segment.Asset?.Asset_Icon_Full_Path && (
                    <AssetImage
                      src={`${AZURE_IMAGE_STORAGE_ACCOUNT_URL_BASE}/${segment.Asset?.Asset_Icon_Full_Path}`}
                    />
                  )}
                </AssetImageDiv>
                {segment.Asset.Asset_Label || ''}
              </div>
            }
            trigger={<Truck color="var(--primary)" size={20} weight="fill" />}
            position={'top center'}
          />
        )}
      </TextCell>,
    ]
    const commonActionCells = [
      RenderTotalDistance(segment),
      RenderTotalDuration(segment),
      <RouteTimeTableActionButtons
        key={`actionbuttons-${segment.idRouteDispatchDriver}`}
        route={segment}
        plan={routeData}
        data={routeData}
      />,
      <TimeCell
        key={`timechart-${segment.idRouteDispatchDriver}`}
        data={segment}
        plan={routeData}
        navigate={navigate}
        earliestTime={earliestTime}
        latestTime={latestTime}
      />,
    ]

    const planningCells = [
      <TextCell
        key={`stops-${segment.idRouteDispatchDriver}`}
        gridArea="totalStops"
      >
        {actualStops?.length || 0}
      </TextCell>,
    ]
    const otherCells = [
      <TextCell
        key={`status-${segment.idRouteDispatchDriver}`}
        gridArea="status"
      >
        <StatusBadge status={segment.segmentStatus || ''} />
      </TextCell>,
      <CompletedCellContainer
        key={`completed-${segment.idRouteDispatchDriver}`}
      >
        <CheckCircle size={16} weight="fill" />
        <span>{completedStops.length}</span> of {actualStops.length || 0}
      </CompletedCellContainer>,
    ]
    //RouteDetail TimeTable Row
    if (isRouteDetail) {
      rowObject = [
        <TimeCell
          key={`timechart-${segment.idRouteDispatchDriver}`}
          data={segment}
          plan={routeData}
          navigate={navigate}
          earliestTime={earliestTime}
          latestTime={latestTime}
        />,
      ]
    } //Planning Status rows
    else if (routeData.planStatus.toLowerCase() === 'planning') {
      rowObject = [...commonInfoCells, ...planningCells, ...commonActionCells]
    } //Dispatched and beyond status rows
    else {
      rowObject = [...commonInfoCells, ...otherCells, ...commonActionCells]
    }

    rowObject && rows.push(rowObject)
  })
  return rows
}

export const getTimeRange = (
  dataWithEndpoints: RoutePlanType | RoutePlanWithColor,
  day: Date,
) => {
  const isMultiDay = !isSameDay(
    dataWithEndpoints.planStartDateTime,
    dataWithEndpoints.planEndDateTime,
  )
  const filteredSegments: Segment[] = []
  const startHours = () => {
    //user selected a date on multi day
    if (isMultiDay && day) {
      return isSameDay(day, dataWithEndpoints.planStartDateTime)
        ? dataWithEndpoints.planStartDateTime.getHours()
        : 0
    }
    //initial load of multiday or single day
    return dataWithEndpoints.planStartDateTime.getHours()
  }

  const endHours = () => {
    //user selected a date on multi day
    if (isMultiDay && day) {
      return isSameDay(day, dataWithEndpoints.planEndDateTime)
        ? dataWithEndpoints.planEndDateTime.getHours()
        : 24
    }
    //initial load of multiday or single day

    return dataWithEndpoints.planEndDateTime.getHours()
  }
  const copyOfDataWithEndpoints = {...dataWithEndpoints}

  copyOfDataWithEndpoints.segments?.forEach(segment => {
    const stops = [
      ...segment.stops.filter(stop => {
        return stop.ServiceEndDateTime && stop.ServiceStartDateTime
          ? isSameDay(stop.ServiceStartDateTime, day) ||
              isSameDay(stop.ServiceEndDateTime, day)
          : false
      }),
    ]
    const newSegment = {...segment, stops: stops}
    filteredSegments.push(newSegment)
  })

  const earliestTime = new Date(startOfDay(day).setHours(startHours()))
  const latestTime = new Date(
    startOfDay(day).setHours(
      endHours() === 24 ? 23 : endHours(),
      endHours() === 24
        ? 59
        : getMinutes(copyOfDataWithEndpoints.planEndDateTime),
    ),
  )

  return {filteredSegments, earliestTime, latestTime}
}
